import { TravelCustomerType } from './../../Models/Travel/index.d'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { format } from 'date-fns'
import { PlanType } from 'Models/Travel'
import { CKYCData } from 'Pages/Sachet/Travel/types'
import { ISPData } from 'Pages/Sachet/Travel/Proposal/SPTagging/ISPData'

export interface FamilyMember {
  relation: number
  dob: string
  readonly?: boolean
  checked: boolean
  firstName?: string
  lastName?: string
  gender?: string
  nationality?: string
  passport?: string
  nomineeFirstName?: string
  nomineeLastName?: string
  nomineeGender?: string
  nomineeDob?: string
  nomineeRelationship?: string
  isNomineeSame?: boolean
  hasAnyPED?: boolean
  hasPedDueToAccidentorGenetics?: boolean
  hasClaimedUnderTravelPolicy?: boolean
  hasPedInLast48Months?: boolean
  pedDueToAccidentorGenetics?: IDisease[]
  pedInLast48Months?: IDisease[]
}

export interface IDisease {
  value: string
}

export interface QoutationDatatype {
  qouteId: string
  selectedPlan: PlanType | null
}
export interface AllDetailsPayload {
  countries: { label: string; value: string; geography_id: number }[]
  familyMembers: FamilyMember[]
  preExistingConditions: boolean
  tripDate: { startDate: string; endDate: string }
  qoutationData: QoutationDatatype
  ckycData?: CKYCData
  currentStep: number
  customer_consent_status: 'initial' | 'pending' | 'approved' | 'rejected'
  customer_consent_id: string | null
  sp_consent_status: 'initial' | 'pending' | 'approved' | 'rejected'
  sp_consent_id: string | null
  travelCustomerData: TravelCustomerType
  selectedSp?: ISPData
}

const initialState: AllDetailsPayload = {
  countries: [],
  tripDate: {
    startDate: '',
    endDate: '',
  },
  selectedSp: {
    label: '',
    value: '',
  },
  familyMembers: [
    {
      relation: 1, // self
      checked: true,
      dob: '',
      readonly: true,
      gender: '',
      isNomineeSame: false,
      nomineeGender: 'male',
      nomineeFirstName: '',
      nomineeLastName: '',
      nomineeDob: '',
      nomineeRelationship: '',
    },
    {
      relation: 16, // spouse
      checked: false,
      dob: '',
      isNomineeSame: true,
      readonly: true,
      gender: '',
      nomineeGender: 'male',
      nomineeDob: '',
      nomineeFirstName: '',
      nomineeLastName: '',
      nomineeRelationship: '',
    },
  ],
  preExistingConditions: false,
  currentStep: 1,
  qoutationData: {
    qouteId: '',
    selectedPlan: null,
  },
  customer_consent_status: 'initial',
  customer_consent_id: null,
  sp_consent_status: 'initial',
  sp_consent_id: null,
  travelCustomerData: {
    firstname: '',
    middlename: '',
    lastname: '',
    phone: '',
    email: '',
    dob: '',
    occupation: '',
    gender: 0,
  },
}

const tripSlice = createSlice({
  name: 'trip',
  initialState,
  reducers: {
    updateCustomerConsentId(state, action: PayloadAction<string | null>) {
      state.customer_consent_id = action.payload
    },
    updateCustomerConsentStatus(state, action: PayloadAction<string>) {
      state.customer_consent_status = action.payload as
        | 'initial'
        | 'pending'
        | 'approved'
        | 'rejected'
    },
    updateSpConsentId(state, action: PayloadAction<string | null>) {
      state.sp_consent_id = action.payload
    },
    updateSpConsentStatus(state, action: PayloadAction<string>) {
      state.sp_consent_status = action.payload
        ? (action.payload as 'initial' | 'pending' | 'approved' | 'rejected')
        : state.sp_consent_status
    },
    updateSelectedSp(state, action: PayloadAction<ISPData>) {
      state.selectedSp = action.payload
    },
    setCurrentStep(state, action: PayloadAction<number>) {
      state.currentStep = action.payload
    },
    updateFamilyMembers: (state, action: PayloadAction<FamilyMember[]>) => {
      state.familyMembers = structuredClone(action.payload)
    },
    updateAllDetails(state, action: PayloadAction<AllDetailsPayload>) {
      const { countries, familyMembers, preExistingConditions, tripDate } = action.payload
      state.countries = countries
      state.familyMembers = familyMembers
      state.preExistingConditions = preExistingConditions
      state.tripDate = tripDate
    },
    updateQoutation(state, action: PayloadAction<QoutationDatatype>) {
      const { selectedPlan, qouteId } = action.payload
      state.qoutationData.qouteId = qouteId
      state.qoutationData.selectedPlan = selectedPlan
    },
    updateTravelCustomer(state, action: PayloadAction<TravelCustomerType>) {
      state.travelCustomerData = action.payload
    },
    resetTripState(state) {
      state.countries = initialState.countries
      state.familyMembers = initialState.familyMembers
      state.preExistingConditions = initialState.preExistingConditions
      state.tripDate = initialState.tripDate
      state.qoutationData = initialState.qoutationData
      state.customer_consent_id = null
      state.customer_consent_status = 'initial'
      state.sp_consent_id = null
      state.sp_consent_status = 'initial'
    },
    updateTravelDetails(state, action: PayloadAction<AllDetailsPayload>) {
      state = action.payload
    },

    setCkycData(state, action: PayloadAction<CKYCData>) {
      state.ckycData = action.payload
    },
  },
})

const { reducer } = tripSlice

export const {
  updateAllDetails,
  setCurrentStep,
  updateFamilyMembers,
  updateQoutation,
  resetTripState,
  setCkycData,
  updateCustomerConsentId,
  updateCustomerConsentStatus,
  updateSpConsentId,
  updateSpConsentStatus,
  updateTravelCustomer,
  updateSelectedSp,
  updateTravelDetails,
} = tripSlice.actions

export { reducer as travelReducer }
